import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Settings, LogOut, Menu, X, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import ThemeToggle from "./ThemeToggle";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2" data-testid="brand-wordmark">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display font-bold text-sm">
        M
      </div>
      <div className="flex flex-col leading-tight">
        <span className="font-display font-bold tracking-tight text-base">MyID BI</span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Subscriptions + Customers
        </span>
      </div>
    </div>
  );
}

function NavList({ onNav }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNav}
          data-testid={testid}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
              isActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 border-r border-border bg-card md:flex md:flex-col">
        <div className="flex h-16 items-center px-5 border-b border-border">
          <Brand />
        </div>
        <div className="flex-1 py-4">
          <NavList />
        </div>
        <div className="border-t border-border p-3">
          <div className="px-3 pb-2 text-xs text-muted-foreground truncate" data-testid="user-email">
            {user?.email}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative h-full w-64 bg-card border-r border-border flex flex-col">
            <div className="flex h-16 items-center justify-between px-5 border-b border-border">
              <Brand />
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="close-drawer">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 py-4">
              <NavList onNav={() => setOpen(false)} />
            </div>
            <div className="border-t border-border p-3">
              <Button variant="ghost" className="w-full justify-start" onClick={handleLogout} data-testid="logout-button-mobile">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="sticky top-0 z-30 glass-header border-b border-border bg-card/70 md:pl-60">
        <div className="flex h-16 items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-2 md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="open-drawer">
              <Menu className="h-5 w-5" />
            </Button>
            <Brand />
          </div>
          <div className="hidden md:block">
            <p className="text-xs text-muted-foreground">Welcome back</p>
            <p className="font-display font-semibold text-base">{user?.name || user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="md:pl-60">
        <div className="px-4 py-6 md:px-8 md:py-10">{children}</div>
      </main>
    </div>
  );
}
